///
/// EarthBrowser
///
/// Created by Matt Giger on 1/19/2024
/// Copyright (c) 2024 Geodata Labs
///

export type rangeType = {
	min: number
	max: number
}

/**
 * Represents a numeric range with a minimum and maximum value.
 */
export class range implements rangeType {
	/** The minimum value of the range */
	readonly min: number
	/** The maximum value of the range */
	readonly max: number

	/**
	 * Creates a new range instance.
	 * @param min - The minimum value of the range (default: positive infinity)
	 * @param max - The maximum value of the range (default: negative infinity)
	 */
	constructor(min: number = Number.POSITIVE_INFINITY, max: number = Number.NEGATIVE_INFINITY) {
		this.min = min
		this.max = max
	}

	/**
	 * Returns a string representation of the range.
	 * @returns A string in the format "[min - max]"
	 */
	public toString(): string {
		return `[${this.min} - ${this.max}]`
	}

	/**
	 * Checks if the range is empty (min >= max).
	 * @returns True if the range is empty, false otherwise
	 */
	public get empty(): boolean {
		return this.min >= this.max
	}

	/**
	 * Checks if a value is within the range.
	 * @param value - The value to check
	 * @returns True if the value is within the range, false otherwise
	 */
	public inRange(value: number): boolean {
		return value >= this.min && value <= this.max
	}

	/**
	 * Calculates a value within the range based on a position and length.
	 * @param pos - The position within the length
	 * @param length - The total length
	 * @returns The calculated value within the range
	 */
	public valueAt(pos: number, length: number): number {
		return this.min + (this.max - this.min) * pos / length
	}

	/**
	 * Calculates the percentage of a value within the range.
	 * @param value - The value to calculate the percentage for
	 * @returns The percentage (0 to 1) of the value within the range
	 */
	public percentAt(value: number): number {
		return (value - this.min) / (this.max - this.min)
	}

	/**
	 * Clamps a value to be within the range.
	 * @param value - The value to clamp
	 * @returns The clamped value
	 */
	public clamp(value: number): number {
		return Math.max(this.min, Math.min(this.max, value))
	}
}